import { ReactNode, useCallback, useMemo } from "react";
import {
    ExpandedState,
    OnChangeFn,
    RowSelectionState,
} from "@tanstack/react-table";
import { ViewConfig } from "@lib/views";
import {
    buildGroupByViewConfig,
    GroupByColumnDef,
} from "./buildGroupByViewConfig";
import { buildGroupedTree, getGroupedRowId } from "./buildGroupedTree";
import {
    canSelectGroupRow,
    canSelectItemRow,
    GROUP_ROW_ID_PREFIX,
    GroupByRow,
    GroupChildrenState,
    GroupDescriptor,
    groupKeyFromRowId,
} from "./groupByRowTypes";
import { makeRenderFullWidthRow } from "./renderGroupFullWidthRow";

export interface UseGroupedViewParams<
    TItem,
    TColumn extends string,
    TSort extends string = never,
> {
    groups: GroupDescriptor[] | null;
    childrenByKey: Record<string, GroupChildrenState<TItem> | undefined>;
    columns: Array<GroupByColumnDef<TItem, TColumn, TSort>>;
    getItemRowId: (item: TItem) => string;
    onExpandGroup: (groupKey: string) => void;
    onLoadMore: (groupKey: string) => void;
    onRetry: (groupKey: string) => void;
    expanded: ExpandedState;
    onExpandedChange: (expanded: ExpandedState) => void;
    skeletonRowCount?: number;
    loadMorePageSize?: number;
    /**
     * Controlled row selection. Omit both to leave ViewTable's internal
     * uncontrolled state in charge (a page that never reads a selection).
     */
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
    /**
     * `"itemsAndGroups"` (default) lets a group-header checkbox cascade to its
     * loaded children. Pass `"items"` when children load lazily and the page
     * has no way to load the rest, so a header checkbox can't imply it
     * selected rows that were never fetched.
     */
    selectableRows?: "items" | "itemsAndGroups";
}

/**
 * The grouped-table slice of ViewTable's props, ready to spread. Owning the
 * easy-to-get-wrong wiring here (`getRowId`, `enableSubRowSelection`, the
 * selection predicate, the expand→fetch dispatch) keeps every grouped page
 * wired identically, while pages keep ViewTable's full surface for everything
 * page-specific (view, footer, tableMeta, refs, ...).
 */
export interface GroupedViewTableProps<
    TItem,
    TColumn extends string,
    TSort extends string = never,
> {
    data: Array<GroupByRow<TItem>> | null;
    config: ViewConfig<GroupByRow<TItem>, TColumn, TSort>;
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    renderFullWidthRow: (row: GroupByRow<TItem>) => ReactNode | null;
    enableRowSelection: (row: GroupByRow<TItem>) => boolean;
    enableSubRowSelection: boolean;
    getRowId: (row: GroupByRow<TItem>) => string;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
}

/**
 * Everything a page needs to render a grouped table with a plain `<ViewTable>`:
 * spread the returned props and add page-specific ones on top. Fires
 * `onExpandGroup` on every collapsed→expanded transition (including
 * re-expands) so the page can ensure that group's fetch is running — handlers
 * must be idempotent.
 */
export function useGroupedView<
    TItem,
    TColumn extends string,
    TSort extends string = never,
>({
    groups,
    childrenByKey,
    columns,
    getItemRowId,
    onExpandGroup,
    onLoadMore,
    onRetry,
    expanded,
    onExpandedChange,
    skeletonRowCount,
    loadMorePageSize,
    rowSelection,
    onRowSelectionChange,
    selectableRows = "itemsAndGroups",
}: UseGroupedViewParams<TItem, TColumn, TSort>): GroupedViewTableProps<
    TItem,
    TColumn,
    TSort
> {
    const config = useMemo(
        () => buildGroupByViewConfig<TItem, TColumn, TSort>({ columns }),
        [columns],
    );

    const data = useMemo(
        () =>
            groups === null
                ? null
                : buildGroupedTree({
                      groups,
                      childrenByKey,
                      getItemRowId,
                      skeletonRowCount,
                      loadMorePageSize,
                  }),
        [
            groups,
            childrenByKey,
            getItemRowId,
            skeletonRowCount,
            loadMorePageSize,
        ],
    );

    const renderFullWidthRow = useMemo(
        () => makeRenderFullWidthRow<TItem>({ onLoadMore, onRetry }),
        [onLoadMore, onRetry],
    );

    const handleExpandedChange = useCallback<OnChangeFn<ExpandedState>>(
        (updater) => {
            const next =
                typeof updater === "function" ? updater(expanded) : updater;
            const prevMap = expanded === true ? {} : expanded;
            // `true` is TanStack's "all rows expanded" sentinel — treat it as
            // every group row being expanded so each group still dispatches.
            const nextRowIds =
                next === true
                    ? (groups ?? []).map(
                          (group) => `${GROUP_ROW_ID_PREFIX}${group.key}`,
                      )
                    : Object.keys(next).filter((rowId) => next[rowId]);
            nextRowIds.forEach((rowId) => {
                if (expanded !== true && !prevMap[rowId]) {
                    const groupKey = groupKeyFromRowId(rowId);
                    if (groupKey !== null) {
                        onExpandGroup(groupKey);
                    }
                }
            });
            onExpandedChange(next);
        },
        [expanded, groups, onExpandGroup, onExpandedChange],
    );

    return {
        data,
        config,
        expanded,
        onExpandedChange: handleExpandedChange,
        renderFullWidthRow,
        enableRowSelection:
            selectableRows === "items" ? canSelectItemRow : canSelectGroupRow,
        enableSubRowSelection: true,
        getRowId: getGroupedRowId,
        rowSelection,
        onRowSelectionChange,
    };
}
